/**
 * Help Center article video links → embed URLs. Only YouTube (played via youtube-nocookie) and
 * Vimeo links with a recognisable video ID are embedded; this never embeds an arbitrary host. The
 * same rules are mirrored in apps/gfgf-studio/lib/help-video-url.ts (the Studio and UI share no
 * package) — edit the two together or the Studio will accept links the Help Center refuses to play.
 */

const YOUTUBE_HOSTS: readonly string[] = [
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
];

const VIMEO_PAGE_HOSTS: readonly string[] = ["vimeo.com", "www.vimeo.com"];

const VIMEO_PLAYER_HOST = "player.vimeo.com";

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH = /^\/(?:shorts|embed)\/([A-Za-z0-9_-]{11})\/?$/;
const YOUTU_BE_PATH = /^\/([A-Za-z0-9_-]{11})\/?$/;
const VIMEO_PAGE_PATH = /^\/(\d+)(?:\/([A-Za-z0-9]+))?\/?$/;
const VIMEO_PLAYER_PATH = /^\/video\/(\d+)\/?$/;
const VIMEO_HASH = /^[A-Za-z0-9]+$/;

function youTubeId(url: URL): string | null {
  if (url.hostname === "youtu.be") return YOUTU_BE_PATH.exec(url.pathname)?.[1] ?? null;
  if (url.pathname === "/watch") {
    const id = url.searchParams.get("v") ?? "";
    return YOUTUBE_ID.test(id) ? id : null;
  }
  return YOUTUBE_PATH.exec(url.pathname)?.[1] ?? null;
}

function vimeoEmbed(id: string, hash: string | null | undefined): string {
  const base = `https://player.vimeo.com/video/${id}`;
  return hash ? `${base}?h=${hash}` : base;
}

/**
 * The privacy-friendly embed URL for a YouTube or Vimeo link, with no query string beyond Vimeo's
 * unlisted-video hash; null for anything else (other hosts, lookalikes, missing IDs, non-URLs).
 */
export function toHelpVideoEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;

  if (YOUTUBE_HOSTS.includes(parsed.hostname)) {
    const id = youTubeId(parsed);
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }

  if (VIMEO_PAGE_HOSTS.includes(parsed.hostname)) {
    const [, id, hash] = VIMEO_PAGE_PATH.exec(parsed.pathname) ?? [];
    return id ? vimeoEmbed(id, hash) : null;
  }

  if (parsed.hostname === VIMEO_PLAYER_HOST) {
    const id = VIMEO_PLAYER_PATH.exec(parsed.pathname)?.[1];
    const hash = parsed.searchParams.get("h");
    if (!id || (hash !== null && !VIMEO_HASH.test(hash))) return null;
    return vimeoEmbed(id, hash);
  }

  return null;
}

/** `mm:ss`, or `h:mm:ss` past an hour. */
export function formatVideoDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = hours > 0 ? String(minutes).padStart(2, "0") : String(minutes);
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
